import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Sidebar } from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const money = (value) => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const currentMonth = () => new Date().toISOString().slice(0, 7);
const initialMonth = () => new URLSearchParams(window.location.search).get('competencia') || currentMonth();

const BalanceteMensal = ({ user, onLogout }) => {
  const company = JSON.parse(localStorage.getItem('company') || '{}');
  const [competence, setCompetence] = useState(initialMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    if (!company.id || !competence) return;
    setLoading(true);
    axiosInstance.get(`/contabil/balancete/${company.id}`, { params: { competencia: competence } })
      .then(({ data: response }) => setData(response))
      .catch((error) => toast.error(error.response?.data?.detail || 'Não foi possível carregar o balancete'))
      .finally(() => setLoading(false));
  }, [company.id, competence]);

  const openLedger = async (account) => {
    if (!account.drill_down_available) return;
    try {
      const { data: response } = await axiosInstance.get(
        `/contabil/balancete/${company.id}/contas/${account.account_id}/razao`,
        { params: { competencia: competence } },
      );
      setLedger(response);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível abrir o razão');
    }
  };

  const rows = [];
  const walk = (nodes) => nodes.forEach((node) => {
    rows.push(node);
    if (expanded[node.account_id]) walk(node.children || []);
  });
  walk(data?.accounts || []);

  return <div className="flex min-h-screen bg-zinc-950 text-white">
    <Sidebar user={user} onLogout={onLogout} />
    <main className="flex-1 p-4 md:p-8 overflow-x-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div><h1 className="text-2xl font-bold">Balancete mensal</h1><p className="text-zinc-400">{company.name || company.razao_social}</p></div>
        <label className="text-sm text-zinc-300">Competência<Input type="month" value={competence} onChange={(e) => setCompetence(e.target.value)} className="mt-1 bg-zinc-900" /></label>
      </div>
      {loading ? <p>Calculando...</p> : data && <>
        <div className="grid md:grid-cols-4 gap-3 mb-5">
          {[['Débitos', data.totals.debits], ['Créditos', data.totals.credits], ['Diferença', data.totals.difference], ['Resultado', data.totals.period_result]].map(([label, value]) =>
            <Card key={label} className="bg-zinc-900 border-zinc-800"><CardHeader className="pb-2"><CardTitle className="text-sm text-zinc-400">{label}</CardTitle></CardHeader><CardContent className="text-lg font-semibold">{money(value)}</CardContent></Card>)}
        </div>
        <div className="mb-3"><Badge className={data.consistency.status === 'INTEGRO' ? 'bg-emerald-700' : 'bg-red-700'}>{data.consistency.status}</Badge></div>
        <div className="border border-zinc-800 rounded-lg overflow-hidden min-w-[900px]">
          <table className="w-full text-sm"><thead className="bg-zinc-900"><tr>{['Conta', 'Saldo anterior', 'Débitos', 'Créditos', 'Saldo final', 'Razão'].map(x => <th key={x} className="p-3 text-left">{x}</th>)}</tr></thead>
            <tbody>{rows.map((row) => <tr key={row.account_id} className="border-t border-zinc-800">
              <td className="p-3" style={{ paddingLeft: `${12 + Math.max(0, row.level - 1) * 18}px` }}>
                {!!row.children?.length && <button className="mr-2" onClick={() => setExpanded(v => ({ ...v, [row.account_id]: !v[row.account_id] }))}>{expanded[row.account_id] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</button>}
                <span className="text-zinc-400 mr-2">{row.code}</span>{row.name}
              </td>
              <td className="p-3">{money(row.previous_balance)}</td><td className="p-3">{money(row.period_debits)}</td><td className="p-3">{money(row.period_credits)}</td><td className="p-3 font-medium">{money(row.final_balance)}</td>
              <td className="p-3"><button disabled={!row.drill_down_available} onClick={() => openLedger(row)} className="disabled:opacity-20"><ExternalLink size={16} /></button></td>
            </tr>)}</tbody></table>
        </div>
        {ledger && <Card className="mt-5 bg-zinc-900 border-zinc-800"><CardHeader><CardTitle>Razão — {ledger.account.codigo} {ledger.account.nome}</CardTitle></CardHeader><CardContent>{ledger.movements.map((movement) => <div key={movement.line.id} className="py-2 border-t border-zinc-800 flex justify-between gap-3"><span>{movement.journal_entry.competence_date} — {movement.journal_entry.description}</span><span>{movement.line.tipo}: {money(movement.line.valor)}</span></div>)}</CardContent></Card>}
      </>}
    </main>
  </div>;
};

export default BalanceteMensal;
